import React from 'react';
import { useTranslation } from 'react-i18next';
import { ValidationError } from 'yup';
import { Button, Flex, Icon } from 'design-system-web';
import { TenantLeaseInfo, Unit } from 'graphql-query-contracts';
import { EditTenantLeaseItem } from './EditTenantLeaseItem';
import { useEditTenantLeases } from '../hooks/useEditTenantLeases';
import * as Sentry from '@sentry/browser';

export type EditTenantLeasesProps = {
  tenantId: string;
  propertyId: string;
  suites: Unit[];
  tenantLeases: TenantLeaseInfo[];
  onCancel: () => void;
  onSave: () => void;
};

export const EditTenantLeases = ({
  tenantId,
  propertyId,
  suites,
  tenantLeases,
  onCancel,
  onSave,
}: EditTenantLeasesProps) => {
  const { t } = useTranslation();

  const {
    leaseItems,
    loading,
    selectedFloors,
    addLeaseItem,
    removeLeaseItem,
    editLeaseItem,
    saveLeaseItems,
  } = useEditTenantLeases({
    tenantId,
    propertyId,
    suites,
    tenantLeases,
  });
  const floorNames = selectedFloors;

  const handleOnSave = async () => {
    try {
      await saveLeaseItems();

      onSave();
    } catch (error) {
      if (error instanceof ValidationError) {
        return;
      }

      Sentry.captureException(error, {
        contexts: {
          params: {
            tenantId,
            propertyId,
          },
        },
      });

      window.Toast.show(
        t(
          'web.admin.channel.settings.tenantManagement.tenant.details.editLeases.saveError'
        )
      );
    }
  };

  return (
    <Flex direction="column" gap={4}>
      <Flex direction="column" gap={2}>
        <b>
          {t(
            'web.admin.channel.settings.tenantManagement.tenant.details.editLeases.floors'
          )}
        </b>
        <span>{floorNames || '-'}</span>
      </Flex>
      <Flex direction="column" gap={5} className="max-w-5xl">
        {leaseItems.map(leaseItem => (
          <EditTenantLeaseItem
            key={leaseItem.id}
            leaseItem={leaseItem}
            removeLeaseItem={removeLeaseItem}
            editLeaseItem={editLeaseItem}
          />
        ))}
      </Flex>
      <Flex>
        <Button
          startIcon={<Icon name="plus" />}
          variant="text"
          testId="add-tenant-lease"
          onClick={addLeaseItem}
        >
          {t(
            'web.admin.channel.settings.tenantManagement.tenant.details.editLeases.assignSuite'
          )}
        </Button>
      </Flex>
      <Flex gap={2}>
        <Button
          variant="primary"
          size="large"
          testId="save-tenant-leases"
          loading={loading}
          onClick={handleOnSave}
        >
          {t(
            'web.admin.channel.settings.tenantManagement.tenant.details.editLeases.save'
          )}
        </Button>
        <Button
          variant="secondary"
          size="large"
          testId="cancel-tenant-leases"
          onClick={onCancel}
        >
          {t(
            'web.admin.channel.settings.tenantManagement.tenant.details.editLeases.cancel'
          )}
        </Button>
      </Flex>
    </Flex>
  );
};
